import {
  RouteProp,
  useIsFocused,
  useLinkProps,
  useNavigation,
  useRoute,
} from '@react-navigation/native';
import { LinearGradient } from 'expo-linear-gradient';
import { fetchMedia } from 'fetch-media';
import { Fragment, useReducer } from 'react';
import {
  Image,
  Linking,
  Platform,
  ScrollView,
  Share,
  StyleSheet,
  View,
} from 'react-native';
import {
  Appbar,
  Button,
  Dialog,
  Portal,
  Surface,
  Text,
} from 'react-native-paper';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ellipse, Path, Svg } from 'react-native-svg';
import { useInaccurateTimestamp } from 'react-native-use-timestamp';
import { useQuery } from 'react-query';
import { AspectRatio } from '../components/AspectRatio';
import { useGoBack } from '../components/BackHeader';
import { PressableSurface } from '../components/PressableSurface';
import { SPECIAL_EVENT_GPVNL_INSTROOM_ID } from '../config';
import { useEvent } from '../events/useEvent';
import { useConfiguration } from '../hooks/useConfiguration';
import { useToken } from '../hooks/useToken';
import { i18n } from '../locale';
import { prepareUrl } from '../navigation/LinkingConfiguration';
import { resetToPath } from '../navigation/utils';
import {
  BACKGROUND_DARK,
  DARK_PURPLE,
  PASTEL_BLUE,
  PASTEL_PINK,
  PASTEL_PURPLE,
} from '../theming';
import { useArtist } from '../tracks/useArtist';
import { RootStackParamList } from '../types';
import { authorization } from '../utils/authorization';
import { useThemedColor } from '../utils/useThemedColor';
import { IMAGE_PIXEL_RATIO, variantImageUrl } from '../utils/variants';
import { useEventRanking } from './useEventRanking';

export function RankingDetailsScreen() {
  const { top, bottom } = useSafeAreaInsets();

  const focused = useIsFocused();
  const { url: eventUrl, details: detailsUrl } =
    useRoute<RouteProp<RootStackParamList, 'RankingDetails'>>().params ?? {};

  const { data: eventData } = useEvent(eventUrl, { enabled: focused });
  const { data: rankingData } = useEventRanking(
    eventUrl + '/rankings/nominal',
    'nominal',
    {
      enabled: Boolean(eventUrl && focused),
      staleTime: 600,
    }
  );

  const entry = rankingData?.ranking._embedded.find(
    (entry) => entry._links.self.href === detailsUrl
  );

  const { data: artistData } = useArtist(
    entry?._embedded.artist._links.self.href,
    { enabled: Boolean(entry?._embedded.artist._links.self.href && focused) }
  );
  const { artist } = artistData || { artist: null };

  const timestamp = useInaccurateTimestamp({ every: 30 * 1000 });
  const started = Boolean(
    eventData?.event.start_at &&
      new Date(timestamp).getTime() >
        new Date(eventData?.event.start_at).getTime()
  );
  const finished = Boolean(
    eventData?.event.end_at &&
      new Date(timestamp).getTime() >
        new Date(eventData?.event.end_at).getTime()
  );

  const open = started && !finished;
  const navigation = useNavigation();
  const share = entry?._links.share?.href.split('/share/').pop();
  const { data } = useConfiguration({ enabled: focused });
  const { token } = useToken();

  const url =
    data && 'invitation' in data._links && share
      ? data._links.invitation.href.replace(
          /{invite}|%7Binvite%7D/,
          encodeURIComponent(share.split('/').reverse()[0])
        )
      : null;

  const { data: shareData, error: shareError } = useQuery({
    queryKey: [url],
    queryFn: () => {
      return fetchMedia(url!, {
        headers: {
          accept: 'application/vnd.soundersmusic.track.share.v1+json',
          authorization: authorization(token!),
        },
      }) as Promise<any>;
    },
    enabled: Boolean(focused && url),
  });

  const cursor = shareData?.share._embedded?.cursor;
  const directPath = cursor
    ? [
        `/events/${prepareUrl(eventData?.event._links.self.href || '')}`,
        `cursor=${encodeURIComponent(cursor)}`,
      ].join('?')
    : null;

  const linkProps = useLinkProps({
    to: [directPath || '/'].filter(Boolean).join('?'),
  });

  const onGoBack = useGoBack(
    `/events/${prepareUrl(eventData?.event._links.self.href || '')}/ranking`
  );

  const uri = variantImageUrl(entry?._links.cover?.href, 'thumbnail');
  const [showingScoreExplanation, toggleScoreExplanation] = useReducer(
    (prev) => !prev,
    false
  );
  const [showingVoteCountExplanation, toggleVoteCountExplanation] = useReducer(
    (prev) => !prev,
    false
  );

  const borderColor = useThemedColor('#0000001a', '#FFFFFF1a');
  const actionColor = useThemedColor(PASTEL_PURPLE, PASTEL_PINK);
  const rankColor = useThemedColor('#1D0F34', PASTEL_BLUE);
  const surfaceColor = useThemedColor('#FFF', DARK_PURPLE);
  const textColor = useThemedColor('#222', '#DDD');
  const scoreBackgroundColor = useThemedColor('#FEE79A', '#FEE79A');

  const maxScore = eventData?.event._links.self.href?.includes(
    SPECIAL_EVENT_GPVNL_INSTROOM_ID
  )
    ? 5
    : 250;

  return (
    <Portal.Host>
      <View
        style={{
          flex: 1,
          position: 'relative',
          width: '100%',
          maxWidth: 600,
          marginHorizontal: 'auto',
          marginTop: 48,
        }}
      >
        <Surface
          style={{
            width: '100%',
            height: '100%',
            maxHeight: Platform.select({ web: '100vh', default: '100%' }),
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            backgroundColor: surfaceColor,
          }}
          elevation={2}
        >
          <View
            style={{
              flexDirection: 'row',
              height: 72,
              justifyContent: 'center',
            }}
          >
            {entry ? (
              <Fragment>
                <View style={{ width: 56, justifyContent: 'center' }}>
                  <Text
                    style={{
                      color: rankColor,
                      fontWeight: '600',
                      fontSize: 24,
                      textAlign: 'center',
                    }}
                  >
                    {entry.ranked ? entry.rank : '-'}
                  </Text>
                </View>
                <View
                  style={{
                    width: 44,
                    marginRight: 12,
                    height: '100%',
                    justifyContent: 'center',
                  }}
                >
                  <Image
                    source={{
                      uri,
                      width: IMAGE_PIXEL_RATIO,
                      height: IMAGE_PIXEL_RATIO,
                    }}
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 4,
                      backgroundColor: '#222',
                    }}
                  />
                </View>

                <View
                  style={{
                    justifyContent: 'center',
                    paddingRight: 12,
                    flex: 1,
                  }}
                >
                  <Text
                    variant="bodySmall"
                    style={{
                      color: rankColor,
                      fontWeight: '300',
                      fontSize: 14,
                    }}
                    numberOfLines={1}
                  >
                    {entry._embedded.artist.name}
                  </Text>
                  <Text
                    variant="bodySmall"
                    style={{
                      color: rankColor,
                      fontWeight: 'bold',
                      fontSize: 14,
                    }}
                    numberOfLines={1}
                  >
                    {entry.title}
                  </Text>
                </View>
              </Fragment>
            ) : null}
            <Appbar.Action
              size={24}
              color={actionColor}
              onPress={onGoBack}
              style={{
                borderRadius: 54 / 2,
                backgroundColor: 'transparent',
                width: 42,
                height: 42,
                marginLeft: 'auto',
              }}
              icon="close"
              accessibilityLabel={i18n.translate(
                'app.ranking.actions.close_details'
              )}
            />
          </View>

          <View
            style={{
              borderTopColor: borderColor,
              borderBottomColor: borderColor,
              borderTopWidth: StyleSheet.hairlineWidth,
              borderBottomWidth: StyleSheet.hairlineWidth,
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-evenly',
              height: 48,
            }}
          >
            <View
              style={{
                flex: 1,
                height: '100%',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Button
                icon="share"
                disabled={!entry?._links.share?.href}
                textColor={actionColor}
                onPress={() =>
                  Share.share(
                    {
                      url: entry?._links.share?.href || '',
                      message: i18n.translate('app.ranking.share_message', {
                        title: entry?.title,
                        url: entry?._links.share?.href || '',
                      }),
                    },
                    {
                      dialogTitle: i18n.translate(
                        'app.ranking.share_dialog_title'
                      ),
                    }
                  )
                }
              >
                {i18n.translate('app.ranking.actions.share')}
              </Button>
            </View>

            <View
              style={{
                borderLeftColor: borderColor,
                borderLeftWidth: StyleSheet.hairlineWidth,
                flex: 1,
                height: '100%',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: surfaceColor,
                position: 'relative',
              }}
            >
              <PressableSurface
                linkProps={{
                  style: {
                    flex: 1,
                    height: '100%',
                    justifyContent: 'center',
                    alignItems: 'center',
                  },
                  onPress: toggleVoteCountExplanation,
                  accessibilityRole: 'button',
                }}
                elevation={0}
              >
                <Text style={{ color: textColor }}>
                  {i18n.translate('app.ranking.votes_count', {
                    value: entry?.vote_count || '-',
                  })}
                </Text>
              </PressableSurface>
            </View>

            <View
              style={{
                borderLeftColor: borderColor,
                borderLeftWidth: StyleSheet.hairlineWidth,
                flex: 1,
                height: '100%',
                justifyContent: 'center',
                position: 'relative',
                backgroundColor: scoreBackgroundColor,
              }}
            >
              <PressableSurface
                linkProps={{
                  style: {
                    flex: 1,
                    height: '100%',
                    justifyContent: 'center',
                    width: '100%',
                    position: 'relative',
                  },
                  onPress: toggleScoreExplanation,
                  accessibilityRole: 'button',
                }}
                style={{
                  flex: 1,
                  height: '100%',
                  justifyContent: 'center',
                  width: '100%',
                }}
                elevation={0}
              >
                <View />
              </PressableSurface>

              <View
                style={[
                  StyleSheet.absoluteFillObject,
                  {
                    zIndex: 1,
                    justifyContent: 'center',
                  },
                ]}
                pointerEvents="none"
              >
                <View
                  style={{
                    backgroundColor: '#F5C100',
                    ...StyleSheet.absoluteFillObject,
                    zIndex: 1,
                    width:
                      Math.round(((entry?.score ?? 0) / maxScore) * 100) + '%',
                  }}
                />
                <Text
                  style={{
                    color: '#000',
                    fontWeight: 'bold',
                    marginHorizontal: 'auto',
                    width: '100%',
                    textAlign: 'center',
                    zIndex: 2,
                    position: 'relative',
                  }}
                >
                  {i18n.translate('app.ranking.score', {
                    value: entry?.score
                      ? Number(entry.score).toFixed(entry.score > 99 ? 0 : 4)
                      : '⏳',
                  })}
                </Text>
              </View>
            </View>
          </View>

          <ScrollView
            style={{
              flex: 1,
              position: 'relative',
              width: '100%',
              maxWidth: 600,
              marginHorizontal: 'auto',
            }}
            contentContainerStyle={{
              width: '100%',
              paddingHorizontal: 24,
              minHeight: '100%',
            }}
          >
            <View style={{ width: '100%' }}>
              <View
                style={{
                  paddingTop: 16,
                  paddingHorizontal: 0,
                  width: '100%',
                }}
              >
                <Button
                  mode="contained"
                  buttonColor="#F5C100"
                  textColor="#000"
                  disabled={!open || !directPath}
                  labelStyle={{ fontWeight: '500' }}
                  contentStyle={{ flexDirection: 'row-reverse', height: 44 }}
                  icon="arrow-right"
                  {...(open ? linkProps : undefined)}
                  onPress={
                    open && directPath
                      ? () => {
                          if ('popToTop' in navigation) {
                            (navigation as any).popToTop();
                          }

                          if (navigation.canGoBack()) {
                            navigation.goBack();
                          }

                          resetToPath(directPath);
                        }
                      : undefined
                  }
                >
                  {i18n.translate('app.ranking.actions.vote')}
                </Button>
              </View>
              {entry?._links.cover?.href ? (
                <View
                  style={{
                    marginHorizontal: 'auto',
                    width: '100%',
                    maxWidth: 420,
                    marginTop: 16,
                  }}
                >
                  <AspectRatio
                    style={{ width: '100%', maxWidth: '100%', aspectRatio: 1 }}
                  >
                    <Surface
                      style={{
                        width: '100%',
                        height: '100%',
                        backgroundColor: DARK_PURPLE,
                        borderRadius: 16,
                        padding: 0,
                      }}
                      elevation={1}
                    >
                      <Image
                        source={{ uri: entry._links.cover.href }}
                        style={{
                          backgroundColor: DARK_PURPLE,
                          borderRadius: 16,
                          width: '100%',
                          height: '100%',
                        }}
                        resizeMode="cover"
                      />
                    </Surface>
                  </AspectRatio>
                </View>
              ) : null}
              <View
                style={{
                  width: '100%',
                  paddingTop: 24,
                  paddingBottom: 32,
                }}
              >
                <Text
                  variant="titleSmall"
                  style={{ fontWeight: 'bold', color: textColor }}
                >
                  {i18n.translate('app.ranking.details_title', {
                    name: artist?.name || entry?._embedded.artist.name || '',
                  })}
                </Text>
                <Text
                  variant="bodySmall"
                  style={{ marginTop: 12, color: textColor }}
                >
                  {artist?.description}
                </Text>
                <View
                  style={{
                    flexWrap: 'wrap',
                    flexDirection: 'row',
                    marginTop: 12,
                  }}
                >
                  <WebsiteLink href={artist?._links.website_url?.href} />
                  <SpotifyLink href={artist?._links.spotify_url?.href} />
                  <FacebookLink href={artist?._links.facebook_url?.href} />
                  <TwitterLink href={artist?._links.twitter_url?.href} />
                  <YouTubeLink href={artist?._links.youtube_url?.href} />
                  <TikTokLink href={artist?._links.tiktok_url?.href} />
                  <InstagramLink href={artist?._links.instagram_url?.href} />
                  <SoundCloudLink href={artist?._links.soundcloud_url?.href} />
                  <BandcampLink href={artist?._links.bandcamp_url?.href} />
                </View>
              </View>
            </View>
          </ScrollView>
        </Surface>
      </View>

      <Dialog
        visible={showingScoreExplanation}
        onDismiss={toggleScoreExplanation}
        style={{ borderRadius: 12 }}
      >
        <Dialog.Title>Over de score</Dialog.Title>
        <Dialog.Content style={{ maxWidth: 480 }}>
          <Text variant="bodyMedium">
            De score is een gewogen berekening van de geldige stemmen op deze
            act.
          </Text>
          <Text variant="bodySmall" style={{ marginTop: 12 }}>
            Bij live shows wordt de online score meegenomen voor een totaal
            score van het live publiek en de online stemmen.
          </Text>
          <Text variant="bodySmall" style={{ marginTop: 12 }}>
            Artiesten krijgen inzicht in het stemgedrag op hun act. Hierover
            ontvangen zij bericht, ongeacht plaatsing in de ranking.
          </Text>
        </Dialog.Content>
        <Dialog.Actions
          style={{
            borderTopColor: borderColor,
            borderTopWidth: StyleSheet.hairlineWidth,
            paddingBottom: 10,
            paddingTop: 8,
            paddingRight: 12,
          }}
        >
          <Button
            onPress={toggleScoreExplanation}
            contentStyle={{ paddingHorizontal: 8 }}
            textColor={useThemedColor(PASTEL_PURPLE, PASTEL_PINK)}
          >
            Sluiten
          </Button>
        </Dialog.Actions>
      </Dialog>

      <Dialog
        visible={showingVoteCountExplanation}
        onDismiss={toggleVoteCountExplanation}
        style={{ borderRadius: 12 }}
      >
        <Dialog.Title>Over het aantal stemmen</Dialog.Title>
        <Dialog.Content style={{ maxWidth: 480 }}>
          <Text variant="bodyMedium">
            Het aantal stemmen geeft weer hoeveel unieke mensen op deze act
            hebben gestemd.
          </Text>
          <Text variant="bodySmall" style={{ marginTop: 12 }}>
            Dit is niet hetzelfde als de score. De score is zichtbaar na afloop
            van een ronde en bepaalt de ranking.
          </Text>
          <Text variant="bodySmall" style={{ marginTop: 12 }}>
            Artiesten krijgen inzicht in het stemgedrag op hun act. Hierover
            ontvangen zij bericht, ongeacht plaatsing in de ranking.
          </Text>
        </Dialog.Content>
        <Dialog.Actions
          style={{
            borderTopColor: borderColor,
            borderTopWidth: StyleSheet.hairlineWidth,
            paddingBottom: 10,
            paddingTop: 8,
            paddingRight: 12,
          }}
        >
          <Button
            onPress={toggleVoteCountExplanation}
            contentStyle={{ paddingHorizontal: 8 }}
            textColor={useThemedColor(PASTEL_PURPLE, PASTEL_PINK)}
          >
            Sluiten
          </Button>
        </Dialog.Actions>
      </Dialog>
    </Portal.Host>
  );
}

function WebsiteLink({ href }: { href: string | undefined }) {
  const linkProps = useLinkProps({ to: href || '' });

  if (!href) {
    return null;
  }

  return (
    <PressableSurface
      style={{
        padding: 10,
        width: 44,
        height: 44,
        borderRadius: 22,
        marginRight: 16,
        marginTop: 12,
        backgroundColor: '#FFF',
      }}
      linkProps={{ ...linkProps, onPress: () => Linking.openURL(href) }}
      accessibilityLabel="Open website"
    >
      <Svg
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        style={{ width: 24, height: 24 }}
      >
        <Path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418"
          stroke="#000"
        />
      </Svg>
    </PressableSurface>
  );
}

function SpotifyLink({ href }: { href: string | undefined }) {
  const linkProps = useLinkProps({ to: href || '' });

  if (!href) {
    return null;
  }

  return (
    <PressableSurface
      style={{
        padding: 10,
        width: 44,
        height: 44,
        borderRadius: 22,
        marginRight: 16,
        marginTop: 12,
        backgroundColor: '#1FD760',
      }}
      linkProps={{ ...linkProps, onPress: () => Linking.openURL(href) }}
      accessibilityLabel="Open Spotify"
    >
      <Svg
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        style={{ width: 24, height: 24 }}
      >
        <Ellipse
          cx="12.0035"
          cy="12.0089"
          rx="10.8922"
          ry="10.8866"
          fill="#000"
        />
        <Path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M11.0434 0.0441844C10.4766 0.10349 10.2253 0.139575 9.58097 0.254608C9.34851 0.296099 8.64569 0.481171 8.31657 0.587677C8.10116 0.65726 7.74598 0.785767 7.60058 0.846671C7.55031 0.867759 7.4132 0.924171 7.2959 0.972209C4.97823 1.92018 2.8586 3.81605 1.54422 6.11639C1.39661 6.37478 1.01721 7.15039 0.926035 7.38015C0.896177 7.45552 0.847962 7.572 0.819018 7.63899C0.636288 8.06144 0.312265 9.19151 0.212255 9.75541C0.192908 9.86427 0.165259 10.015 0.150863 10.0904C0.121081 10.2458 0.0743887 10.6492 0.029449 11.1386C-0.00970192 11.5653 -0.00985414 12.5346 0.0292206 12.916C0.0916031 13.5252 0.127174 13.7995 0.185976 14.1253C0.241961 14.4351 0.394375 15.0992 0.458509 15.3129C0.546485 15.6061 0.800204 16.353 0.820541 16.3787C0.833718 16.3955 0.854741 16.4434 0.867156 16.4853C0.888712 16.558 0.910877 16.6108 1.0652 16.9573C2.44477 20.0542 5.53937 22.7481 8.60601 23.5217C8.65628 23.5343 8.86194 23.588 9.06302 23.6408C10.8661 24.1148 13.1907 24.1201 14.9204 23.6542C15.0084 23.6305 15.1592 23.5901 15.2556 23.5644C17.87 22.8682 20.4181 21.0274 21.9563 18.7235C22.3523 18.1304 23.0324 16.8841 23.0324 16.7516C23.0324 16.7291 23.0441 16.6976 23.0583 16.6817C23.1164 16.617 23.4604 15.6505 23.5646 15.2596C23.5903 15.1633 23.631 15.0121 23.655 14.9236C24.1188 13.2116 24.1146 10.9025 23.6444 9.08546C23.5946 8.89286 23.545 8.70101 23.5343 8.65914C22.9138 6.23264 20.9945 3.58865 18.767 2.09208C18.507 1.91736 18.0376 1.62472 18.0175 1.62472C18.0101 1.62472 17.9516 1.5916 17.8874 1.5511C17.761 1.47139 17.1911 1.18126 16.9694 1.08382C16.894 1.05062 16.7432 0.985761 16.6342 0.93955C16.5253 0.893415 16.3913 0.836013 16.3366 0.812108C16.2297 0.76544 15.5516 0.537126 15.4003 0.49693C13.8033 0.072124 12.2853 -0.0856174 11.0434 0.0441844ZM11.866 6.40744C12.4953 6.45266 13.4559 6.55376 13.8464 6.61588C13.947 6.63187 14.1294 6.65966 14.2518 6.6777C16.1965 6.96395 18.3563 7.67203 19.9704 8.55255C20.7162 8.95939 20.9665 9.47868 20.7185 10.1048C20.4946 10.6703 19.7287 10.9695 19.2087 10.6945C18.7665 10.4607 17.9115 10.0336 17.792 9.98684C17.7166 9.9573 17.6138 9.91528 17.5635 9.89358C17.0915 9.68925 16.0726 9.3622 15.4308 9.20895C15.347 9.189 15.203 9.1539 15.1108 9.13114C14.9213 9.08417 14.2992 8.96624 13.755 8.87413C11.1083 8.42587 7.75756 8.55035 5.45261 9.18238C5.06857 9.28767 4.97899 9.30198 4.76709 9.29162C3.68313 9.23879 3.31942 7.83944 4.23391 7.24022C4.68285 6.94598 6.83607 6.53739 8.60601 6.41048C9.14734 6.37166 11.3396 6.36968 11.866 6.40744ZM10.9673 10.5158C12.5835 10.6 14.1471 10.8897 15.6322 11.38C17.3156 11.9358 18.8187 12.7176 19.0198 13.1418C19.451 14.052 18.4316 14.866 17.5787 14.2924C17.3182 14.1171 16.456 13.6901 15.9792 13.5002C14.087 12.7466 12.0783 12.3733 9.9213 12.3747C8.50889 12.3756 7.27495 12.5421 5.84869 12.9242C5.46381 13.0273 5.33257 13.0274 5.09995 12.9246C4.2956 12.5689 4.34976 11.4593 5.18541 11.1758C6.65631 10.6767 8.97497 10.4121 10.9673 10.5158ZM11.3393 14.4452C11.5117 14.462 11.7967 14.4902 11.9727 14.5078C13.3273 14.6429 14.8724 15.078 16.1315 15.6789C16.5581 15.8826 17.2661 16.2775 17.3581 16.3632C17.9192 16.8856 17.4691 17.802 16.7205 17.6616C16.6642 17.6511 16.4539 17.5495 16.253 17.436C14.6977 16.557 13.2168 16.1144 11.2872 15.9516C10.4764 15.8833 8.89827 15.9139 8.24543 16.0106C8.19241 16.0184 8.03931 16.0402 7.90525 16.059C7.47619 16.1191 6.87583 16.216 6.65608 16.2605C6.53878 16.2843 6.30593 16.3325 6.13859 16.3676C5.43898 16.5145 5.04274 16.2654 5.04175 15.6783C5.04092 15.2218 5.24056 15.0343 5.87916 14.8917C7.59532 14.5082 9.98452 14.3129 11.3393 14.4452Z"
          fill="#1FD760"
        />
      </Svg>
    </PressableSurface>
  );
}

function FacebookLink({ href }: { href: string | undefined }) {
  const linkProps = useLinkProps({ to: href || '' });

  if (!href) {
    return null;
  }

  return (
    <PressableSurface
      style={{
        padding: 10,
        width: 44,
        height: 44,
        borderRadius: 22,
        marginRight: 16,
        marginTop: 12,
        backgroundColor: '#1877F2',
      }}
      linkProps={{ ...linkProps, onPress: () => Linking.openURL(href) }}
      accessibilityLabel="Open Facebook"
    >
      <Svg
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        style={{ width: 22, height: 20 }}
      >
        <Path
          d="M24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 17.9895 4.38822 22.954 10.125 23.8542V15.4688H7.07813V12H10.125V9.35626C10.125 6.34875 11.9165 4.68751 14.6576 4.68751C15.9705 4.68751 17.3438 4.92188 17.3438 4.92188V7.875H15.8306C14.3399 7.875 13.875 8.80002 13.875 9.74901V12H17.2031L16.6711 15.4688H13.875V23.8542C19.6118 22.954 24 17.9895 24 12Z"
          fill="#1877F2"
        />
        <Path
          d="M16.6711 15.4687L17.2031 12H13.875V9.74898C13.875 8.80001 14.3399 7.875 15.8306 7.875H17.3438V4.92187C17.3438 4.92187 15.9705 4.6875 14.6576 4.6875C11.9165 4.6875 10.125 6.34874 10.125 9.35625V12H7.07812V15.4687H10.125V23.8542C10.7453 23.9514 11.3722 24.0002 12 24C12.6379 24 13.264 23.9501 13.875 23.8542V15.4687H16.6711Z"
          fill="white"
        />
      </Svg>
    </PressableSurface>
  );
}

function TwitterLink({ href }: { href: string | undefined }) {
  const linkProps = useLinkProps({ to: href || '' });

  if (!href) {
    return null;
  }

  return (
    <PressableSurface
      style={{
        padding: 13,
        width: 44,
        height: 44,
        borderRadius: 22,
        marginRight: 16,
        marginTop: 12,
        backgroundColor: '#2AA9E0',
      }}
      linkProps={{ ...linkProps, onPress: () => Linking.openURL(href) }}
      accessibilityLabel="Open Twitter"
    >
      <Svg
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        style={{ width: 20, height: 20 }}
      >
        <Path
          d="M24 4.3088C23.117 4.70048 22.168 4.96508 21.1721 5.08413C22.1886 4.47476 22.9694 3.50984 23.337 2.36C22.3855 2.92436 21.3318 3.33404 20.2102 3.55484C19.3121 2.59784 18.0324 2 16.6161 2C13.8969 2 11.6922 4.20452 11.6922 6.92373C11.6922 7.30965 11.7357 7.68549 11.8197 8.04585C7.72749 7.84053 4.09941 5.88021 1.67084 2.9012C1.247 3.6284 1.00412 4.47417 1.00412 5.37657C1.00412 7.08489 1.8734 8.59197 3.1946 9.47493C2.38748 9.44937 1.62824 9.22785 0.964401 8.85909C0.963841 8.87961 0.963841 8.90025 0.963841 8.92101C0.963841 11.3066 2.66113 13.2966 4.91365 13.7492C4.50049 13.8618 4.06549 13.9219 3.61644 13.9219C3.29916 13.9219 2.99077 13.8909 2.69005 13.8336C3.31669 15.7897 5.13505 17.2133 7.28977 17.2529C5.60461 18.5736 3.48157 19.3608 1.17456 19.3608C0.777121 19.3608 0.385201 19.3374 0 19.2919C2.17908 20.6891 4.76725 21.5042 7.54789 21.5042C16.6047 21.5042 21.5574 14.0015 21.5574 7.49457C21.5574 7.28109 21.5526 7.06881 21.5432 6.85749C22.5051 6.1634 23.3398 5.29628 23.9999 4.3088H24Z"
          fill="#FFF"
        />
      </Svg>
    </PressableSurface>
  );
}

function YouTubeLink({ href }: { href: string | undefined }) {
  const linkProps = useLinkProps({ to: href || '' });

  if (!href) {
    return null;
  }

  return (
    <PressableSurface
      style={{
        padding: 10,
        width: 44,
        height: 44,
        borderRadius: 22,
        marginRight: 16,
        marginTop: 12,
        backgroundColor: '#FF0000',
      }}
      linkProps={{ ...linkProps, onPress: () => Linking.openURL(href) }}
      accessibilityLabel="Open YouTube"
    >
      <Svg
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        style={{ width: 24, height: 24 }}
      >
        <Path
          d="M23.4982 7.62364C23.2216 6.59039 22.4096 5.7784 21.3763 5.50181C19.5055 5 12 5 12 5C12 5 4.49456 5 2.62364 5.50181C1.59039 5.7784 0.778399 6.59039 0.501811 7.62364C1.50211e-07 9.49456 0 13.4004 0 13.4004C0 13.4004 1.50211e-07 17.3062 0.501811 19.1772C0.778399 20.2104 1.59039 21.0224 2.62364 21.2989C4.49456 21.8008 12 21.8008 12 21.8008C12 21.8008 19.5055 21.8008 21.3763 21.2989C22.4096 21.0224 23.2216 20.2104 23.4982 19.1772C24 17.3062 24 13.4004 24 13.4004C24 13.4004 23.998 9.49456 23.4982 7.62364Z"
          fill="#FFF"
        />
        <Path
          d="M9.59766 17.0003L15.8328 13.4008L9.59766 9.80115V17.0003Z"
          fill="#FF0000"
        />
      </Svg>
    </PressableSurface>
  );
}

function TikTokLink({ href }: { href: string | undefined }) {
  const linkProps = useLinkProps({ to: href || '' });

  if (!href) {
    return null;
  }

  return (
    <PressableSurface
      style={{
        padding: 10,
        width: 44,
        height: 44,
        borderRadius: 22,
        marginRight: 16,
        backgroundColor: '#000000',
        marginTop: 12,
      }}
      linkProps={{ ...linkProps, onPress: () => Linking.openURL(href) }}
      accessibilityLabel="Open TikTok"
    >
      <Svg
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        style={{ width: 24, height: 24 }}
      >
        <Path
          d="M17.1868 8.64516C18.7332 9.74998 20.6275 10.4 22.6735 10.4V6.46494C22.2863 6.46502 21.9001 6.42467 21.5213 6.34445V9.44193C19.4754 9.44193 17.5813 8.79187 16.0345 7.68713V15.7175C16.0345 19.7347 12.7763 22.9911 8.75729 22.9911C7.2577 22.9911 5.86389 22.538 4.70605 21.7608C6.02753 23.1113 7.87044 23.9491 9.90929 23.9491C13.9285 23.9491 17.187 20.6927 17.187 16.6754V8.64516H17.1868ZM18.6082 4.67513C17.8179 3.8122 17.2991 2.69702 17.1868 1.46414V0.958008H16.0949C16.3697 2.52499 17.3072 3.86373 18.6082 4.67513ZM7.24808 18.6781C6.80655 18.0995 6.56795 17.3917 6.56901 16.6639C6.56901 14.8266 8.05931 13.3368 9.89794 13.3368C10.2406 13.3367 10.5812 13.3892 10.9078 13.4927V9.46971C10.5261 9.41743 10.141 9.39524 9.756 9.40338V12.5347C9.42921 12.4312 9.08844 12.3786 8.7457 12.3789C6.90707 12.3789 5.41685 13.8684 5.41685 15.706C5.41685 17.0053 6.16175 18.1302 7.24808 18.6781Z"
          fill="#FF004F"
        />
        <Path
          d="M16.0344 7.68705C17.5812 8.79179 19.4753 9.44184 21.5212 9.44184V6.34436C20.3792 6.10124 19.3682 5.50478 18.6081 4.67513C17.307 3.86365 16.3696 2.52491 16.0948 0.958008H13.2266V16.6752C13.2201 18.5075 11.7324 19.9912 9.89767 19.9912C8.81652 19.9912 7.85603 19.4761 7.24773 18.6781C6.16148 18.1302 5.41658 17.0052 5.41658 15.7061C5.41658 13.8687 6.9068 12.379 8.74543 12.379C9.0977 12.379 9.43723 12.4338 9.75573 12.5348V9.40346C5.80732 9.48499 2.63184 12.7095 2.63184 16.6753C2.63184 18.655 3.4226 20.4496 4.70603 21.7609C5.86386 22.538 7.25767 22.9912 8.75726 22.9912C12.7764 22.9912 16.0345 19.7346 16.0345 15.7175V7.68705H16.0344Z"
          fill="#FFFFFF"
        />
        <Path
          d="M21.521 6.3443V5.50677C20.4912 5.50833 19.4816 5.22008 18.6079 4.67499C19.3814 5.52132 20.3998 6.10488 21.521 6.3443ZM16.0946 0.957945C16.0684 0.808192 16.0482 0.657452 16.0343 0.506137V0H12.0741V15.7174C12.0678 17.5494 10.5801 19.0331 8.74525 19.0331C8.20656 19.0331 7.69796 18.9053 7.24755 18.6782C7.85585 19.476 8.81634 19.991 9.89749 19.991C11.732 19.991 13.22 18.5075 13.2264 16.6752V0.957945H16.0946ZM9.75571 9.4034V8.51178C9.42481 8.46657 9.09119 8.44389 8.75716 8.44405C4.73774 8.44397 1.47949 11.7005 1.47949 15.7174C1.47949 18.2357 2.76004 20.4552 4.70593 21.7607C3.42251 20.4495 2.63174 18.6547 2.63174 16.6751C2.63174 12.7095 5.80714 9.48493 9.75571 9.4034Z"
          fill="#00F2EA"
        />
      </Svg>
    </PressableSurface>
  );
}

function InstagramLink({ href }: { href: string | undefined }) {
  const linkProps = useLinkProps({ to: href || '' });

  if (!href) {
    return null;
  }

  return (
    <PressableSurface
      style={{
        padding: 6,
        width: 44,
        height: 44,
        borderRadius: 22,
        marginRight: 16,
        marginTop: 12,
        backgroundColor: 'black',
        overflow: 'hidden',
      }}
      linkProps={{ ...linkProps, onPress: () => Linking.openURL(href) }}
      accessibilityLabel="Open Instagram"
    >
      <LinearGradient
        colors={['#8D3CC6', '#E72661', '#F98D5F']}
        locations={[0.1223, 0.4963, 0.8595]}
        style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
      />
      <Svg
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        style={{ width: 32, height: 32, position: 'relative' }}
      >
        <Path
          d="M11.9993 3.13867C9.59309 3.13867 9.29112 3.1492 8.34606 3.19222C7.40285 3.23542 6.75904 3.38479 6.1957 3.60394C5.61298 3.8303 5.11867 4.13309 4.62621 4.62587C4.13338 5.11847 3.83066 5.61291 3.60363 6.19561C3.38398 6.75929 3.23447 7.40346 3.19202 8.34656C3.14975 9.29187 3.13867 9.59411 3.13867 12.001C3.13867 14.4078 3.14938 14.7089 3.1922 15.6543C3.23558 16.5977 3.3849 17.2417 3.60382 17.8052C3.8303 18.3881 4.13301 18.8825 4.62565 19.3751C5.11793 19.8681 5.61224 20.1716 6.19459 20.398C6.7583 20.6171 7.4023 20.7665 8.34532 20.8097C9.29038 20.8527 9.59217 20.8632 11.9982 20.8632C14.4046 20.8632 14.7056 20.8527 15.6507 20.8097C16.5939 20.7665 17.2384 20.6171 17.8021 20.398C18.3847 20.1716 18.8782 19.8681 19.3705 19.3751C19.8633 18.8825 20.1661 18.3881 20.3931 17.8054C20.6109 17.2417 20.7604 16.5975 20.8047 15.6544C20.8472 14.7091 20.8582 14.4078 20.8582 12.001C20.8582 9.59411 20.8472 9.29205 20.8047 8.34674C20.7604 7.40328 20.6109 6.75929 20.3931 6.19579C20.1661 5.61291 19.8633 5.11847 19.3705 4.62587C18.8777 4.13291 18.3849 3.83012 17.8016 3.60394C17.2368 3.38479 16.5926 3.23542 15.6494 3.19222C14.7043 3.1492 14.4034 3.13867 11.9965 3.13867H11.9993ZM11.2045 4.73573C11.4404 4.73536 11.7036 4.73573 11.9993 4.73573C14.3649 4.73573 14.6452 4.74422 15.5794 4.78669C16.4432 4.8262 16.9121 4.97058 17.2244 5.09188C17.6379 5.25251 17.9326 5.44453 18.2425 5.75471C18.5526 6.06489 18.7446 6.3603 18.9056 6.77387C19.0268 7.0859 19.1714 7.55486 19.2107 8.41893C19.2531 9.35317 19.2623 9.63381 19.2623 11.9989C19.2623 14.3641 19.2531 14.6447 19.2107 15.5789C19.1712 16.443 19.0268 16.912 18.9056 17.224C18.745 17.6376 18.5526 17.932 18.2425 18.242C17.9324 18.5522 17.638 18.7442 17.2244 18.9049C16.9125 19.0267 16.4432 19.1707 15.5794 19.2102C14.6454 19.2527 14.3649 19.2619 11.9993 19.2619C9.63351 19.2619 9.35313 19.2527 8.41916 19.2102C7.55532 19.1704 7.08648 19.026 6.77399 18.9047C6.36053 18.7441 6.0652 18.552 5.7551 18.2419C5.44501 17.9317 5.25304 17.637 5.09209 17.2232C4.97082 16.9112 4.82629 16.4423 4.78698 15.5782C4.74452 14.644 4.73603 14.3633 4.73603 11.9967C4.73603 9.63011 4.74452 9.35095 4.78698 8.41672C4.82648 7.55265 4.97082 7.08368 5.09209 6.77129C5.25267 6.35771 5.44501 6.0623 5.7551 5.75212C6.0652 5.44194 6.36053 5.24993 6.77399 5.08893C7.0863 4.96707 7.55532 4.82306 8.41916 4.78337C9.23648 4.74644 9.55322 4.73536 11.2045 4.73351V4.73573ZM16.7286 6.20724C16.1416 6.20724 15.6654 6.68303 15.6654 7.27034C15.6654 7.85747 16.1416 8.33382 16.7286 8.33382C17.3156 8.33382 17.7918 7.85747 17.7918 7.27034C17.7918 6.68322 17.3156 6.20687 16.7286 6.20687V6.20724ZM11.9993 7.44981C9.48659 7.44981 7.44937 9.48758 7.44937 12.001C7.44937 14.5143 9.48659 16.5512 11.9993 16.5512C14.512 16.5512 16.5485 14.5143 16.5485 12.001C16.5485 9.48758 14.5118 7.44981 11.9991 7.44981H11.9993ZM11.9993 9.04686C13.6302 9.04686 14.9526 10.3694 14.9526 12.001C14.9526 13.6324 13.6302 14.9551 11.9993 14.9551C10.3681 14.9551 9.04599 13.6324 9.04599 12.001C9.04599 10.3694 10.3681 9.04686 11.9993 9.04686Z"
          fill="white"
        />
      </Svg>
    </PressableSurface>
  );
}

function SoundCloudLink({ href }: { href: string | undefined }) {
  const linkProps = useLinkProps({ to: href || '' });

  if (!href) {
    return null;
  }

  return (
    <PressableSurface
      style={{
        padding: 6,
        width: 44,
        height: 44,
        borderRadius: 22,
        marginRight: 16,
        marginTop: 12,
        backgroundColor: '#FF5500',
      }}
      linkProps={{ ...linkProps, onPress: () => Linking.openURL(href) }}
      accessibilityLabel="Open SoundCloud"
    >
      <Svg
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        style={{ width: 32, height: 32 }}
      >
        <Path
          d="M12.2378 5.55627C12.1064 5.55627 12 5.6627 12 5.794V16.806C12 17.0712 12.215 17.2862 12.4802 17.2862H15.8585C18.0322 17.2862 19.7942 15.5241 19.7942 13.3505C19.7942 11.1769 18.0322 9.4148 15.8585 9.4148V9.17707C15.8585 7.17736 14.2374 5.55627 12.2378 5.55627Z"
          fill="white"
        />
        <Path
          d="M9.99678 6.21222C9.99678 5.93519 10.2214 5.71062 10.4984 5.71062C10.7754 5.71062 11 5.93519 11 6.21222V16.7846C11 17.0616 10.7754 17.2862 10.4984 17.2862C10.2214 17.2862 9.99678 17.0616 9.99678 16.7846V6.21222Z"
          fill="white"
        />
        <Path
          d="M8.49839 6.86817C8.22136 6.86817 7.99678 7.09275 7.99678 7.36978V16.7846C7.99678 17.0616 8.22136 17.2862 8.49839 17.2862C8.77542 17.2862 9 17.0616 9 16.7846V7.36978C9 7.09275 8.77542 6.86817 8.49839 6.86817Z"
          fill="white"
        />
        <Path
          d="M6 10.1479C6 9.87088 6.22458 9.64631 6.50161 9.64631C6.77864 9.64631 7.00321 9.87088 7.00321 10.1479V16.7846C7.00321 17.0616 6.77864 17.2862 6.50161 17.2862C6.22458 17.2862 6 17.0616 6 16.7846V10.1479Z"
          fill="white"
        />
        <Path
          d="M4.50161 10.7267C4.22458 10.7267 4 10.9513 4 11.2283V15.3955C4 15.6725 4.22458 15.8971 4.50161 15.8971C4.77864 15.8971 5.00322 15.6725 5.00322 15.3955V11.2283C5.00322 10.9513 4.77864 10.7267 4.50161 10.7267Z"
          fill="white"
        />
      </Svg>
    </PressableSurface>
  );
}

function BandcampLink({ href }: { href: string | undefined }) {
  const linkProps = useLinkProps({ to: href || '' });

  if (!href) {
    return null;
  }

  return (
    <PressableSurface
      style={{
        padding: 10,
        width: 44,
        height: 44,
        borderRadius: 22,
        marginRight: 16,
        marginTop: 12,
        backgroundColor: '#FFF',
      }}
      linkProps={{ ...linkProps, onPress: () => Linking.openURL(href) }}
      accessibilityLabel="Open Bandcamp"
    >
      <Svg
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        style={{ width: 24, height: 24 }}
      >
        <Path d="M16.36 19.1108H0L7.63987 5H24L16.36 19.1108Z" fill="#6399A8" />
      </Svg>
    </PressableSurface>
  );
}
